import React, { useEffect, useState, memo } from "react";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import FormHelperText from "@material-ui/core/FormHelperText";
import Typography from "@material-ui/core/Typography";
import { toast } from "react-toastify";
import { registerPushNotifications } from "../../services/pushNotifications";

/**
 * Botão para registar Web Push no utilizador atual (salva subscription na API).
 * Visível para admin em Configurações → Opções e para atendentes na mesma página dedicada (perfil user).
 */
function BrowserPushCard({
  /** Se true, envolve em Grid item como na matriz antiga de Options */
  gridItem = true,
  /** Página só de notificações (atendentes): título curto ajuda o scan visual */
  prominent = false,
}) {
  const [browserPushEnabled, setBrowserPushEnabled] = useState(false);
  const [loadingBrowserPushEnabled, setLoadingBrowserPushEnabled] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const accepted = localStorage.getItem("push-notification-accepted") === "true";
        const permission = typeof Notification !== "undefined" ? Notification.permission : "default";
        if ((accepted || permission === "granted") && "serviceWorker" in navigator && "PushManager" in window) {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          setBrowserPushEnabled(!!sub);
        } else {
          setBrowserPushEnabled(false);
        }
      } catch {
        setBrowserPushEnabled(false);
      }
    })();
  }, []);

  const inner = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {prominent ? (
        <>
          <Typography variant="subtitle2" color="textSecondary">
            Você precisa clicar uma vez aqui e permitir alertas no navegador ou na PWA instalada.
          </Typography>
          <Typography variant="caption" color="textSecondary" component="p" style={{ marginBottom: 8 }}>
            Mesmo que o navegador já tenha permissão genérica, é preciso concluir este passo para gravar no
            servidor — senão não chegam notificações novas mensagens ou tickets na sua fila.
          </Typography>
        </>
      ) : null}
      <Button
        variant="contained"
        color={browserPushEnabled ? "default" : "primary"}
        disabled={loadingBrowserPushEnabled || browserPushEnabled}
        fullWidth={prominent}
        onClick={async () => {
          try {
            setLoadingBrowserPushEnabled(true);
            const result = await registerPushNotifications();
            if (result.ok) {
              setBrowserPushEnabled(true);
              toast.success("Notificações do navegador ativadas.");
            } else {
              setBrowserPushEnabled(false);
              toast.error(result.message || "Não foi possível ativar notificações.");
            }
          } catch {
            setBrowserPushEnabled(false);
            toast.error("Erro ao ativar notificações.");
          } finally {
            setLoadingBrowserPushEnabled(false);
          }
        }}
      >
        {browserPushEnabled
          ? "Notificações ativadas"
          : loadingBrowserPushEnabled
            ? "Ativando..."
            : "Ativar notificações do navegador"}
      </Button>
      <FormHelperText component="span">
        {browserPushEnabled
          ? "Você vai receber notificações quando houver novas mensagens/tickets."
          : "Clique para aceitar a permissão do navegador e habilitar push."}
      </FormHelperText>
    </div>
  );

  if (!gridItem) {
    return inner;
  }

  return (
    <Grid xs={12} sm={prominent ? 12 : 6} md={prominent ? 12 : 4} item>
      {inner}
    </Grid>
  );
}

export default memo(BrowserPushCard);
